import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { fmtMoney, fmtDate } from '../lib/format';
import { STICKY_COLORS, stickyColorFor, clientColorFor, isHexColor, normalizeHex } from '../lib/stickyColors';
import EmojiPicker from '../components/EmojiPicker';
import { Briefcase, Plus, Trash2, Edit2, Users, FileText, Clock, Zap, Download, Upload, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { NumberInput } from '../components/ui/number-input';
import { toast } from 'sonner';

const STATUSES = [
  { id: 'active', label: 'active', color: '#059669' },
  { id: 'ad_hoc', label: 'ad hoc project', color: '#d394ab' },
  { id: 'paused', label: 'paused', color: '#f59e0b' },
  { id: 'ended', label: 'ended', color: '#94a3b8' },
];

const PROJECT_STATUSES = [
  { id: 'todo', label: 'to do', color: '#94a3b8' },
  { id: 'in_progress', label: 'in progress', color: '#38bdf8' },
  { id: 'done', label: 'done', color: '#059669' },
  { id: 'invoiced', label: 'invoiced', color: '#d394ab' },
];

const todayMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (m, delta) => {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

const emptyClient = () => ({
  name: '', contact_name: '', contact_email: '', contact_phone: '', handle: '',
  status: 'active', currency: 'GBP', notes: '', logo: '',
  packages: [], ad_hoc_projects: [], insight_types: [],
});
const emptyPkg = (type = 'retainer') => ({
  id: 'pkg_' + Math.random().toString(36).slice(2, 10),
  name: '', type, price: 0, hourly_rate: 0, hours_log: [], includes: '',
  roll_date: '', start_date: '', end_date: '',
});
const emptyProject = () => ({
  id: 'proj_' + Math.random().toString(36).slice(2, 10),
  title: '', fee: 0, status: 'todo', due_date: '', notes: '', bill_month: todayMonth(),
});
const emptyHours = () => ({
  id: 'hl_' + Math.random().toString(36).slice(2, 10),
  month: todayMonth(), hours: 0, notes: '',
});

// ---------- Package editor ----------
const PackageEditor = ({ pkg, onPatch, onRemove, currency }) => {
  const isHourly = pkg.type === 'hourly';

  const patchLog = (idx, patch) => onPatch({ hours_log: (pkg.hours_log || []).map((h, i) => i === idx ? { ...h, ...patch } : h) });
  const addLog = () => onPatch({ hours_log: [...(pkg.hours_log || []), emptyHours()] });
  const removeLog = (idx) => onPatch({ hours_log: (pkg.hours_log || []).filter((_, i) => i !== idx) });

  const totalHours = (pkg.hours_log || []).reduce((s, h) => s + (Number(h.hours) || 0), 0);
  const totalHourlyValue = totalHours * (Number(pkg.hourly_rate) || 0);

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid={`pkg-editor-${pkg.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-start">
        <div>
          <Label className="text-[11px]">Type</Label>
          <Select value={pkg.type || 'retainer'} onValueChange={(v) => onPatch({ type: v })}>
            <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="retainer"><span className="inline-flex items-center gap-1.5"><Zap className="w-3 h-3 text-pink-500" /> retainer</span></SelectItem>
              <SelectItem value="hourly"><span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3 text-lilac-600" /> hourly</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Service</Label>
          <Select value={pkg.service || 'organic_social'} onValueChange={(v) => onPatch({ service: v })}>
            <SelectTrigger className="mt-1 h-8 text-sm" data-testid={`pkg-service-${pkg.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="organic_social">🌱 organic social</SelectItem>
              <SelectItem value="influencer">✨ influencer</SelectItem>
              <SelectItem value="other">other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px]">Package name</Label>
          <Input value={pkg.name} onChange={(e) => onPatch({ name: e.target.value })} placeholder={isHourly ? 'e.g. Bec — social hours' : 'e.g. Content + strategy'} className="mt-1 h-8 text-sm" />
        </div>
        {isHourly ? (
          <div>
            <Label className="text-[11px]">Rate £/hr</Label>
            <NumberInput value={pkg.hourly_rate} onChange={(v) => onPatch({ hourly_rate: Number(v) || 0 })} className="mt-1 h-8 text-sm" data-testid={`pkg-rate-${pkg.id}`} />
          </div>
        ) : (
          <div>
            <Label className="text-[11px]">Price / month</Label>
            <NumberInput value={pkg.price} onChange={(v) => onPatch({ price: Number(v) || 0 })} className="mt-1 h-8 text-sm" data-testid={`pkg-price-${pkg.id}`} />
          </div>
        )}
        <div>
          <Label className="text-[11px]">Start date</Label>
          <Input type="date" value={pkg.start_date || ''} onChange={(e) => onPatch({ start_date: e.target.value })} className="mt-1 h-8 text-sm" />
        </div>
        <div className="flex items-end justify-between gap-1">
          <div className="flex-1">
            <Label className="text-[11px]">End date <span className="text-slate-400">(optional)</span></Label>
            <Input type="date" value={pkg.end_date || ''} onChange={(e) => onPatch({ end_date: e.target.value })} className="mt-1 h-8 text-sm" />
          </div>
          <button onClick={onRemove} className="mb-0.5 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="remove package"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {!isHourly && (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px]">Roll date <span className="text-slate-400">(renews on)</span></Label>
            <Input type="date" value={pkg.roll_date || ''} onChange={(e) => onPatch({ roll_date: e.target.value })} className="mt-1 h-8 text-sm" />
          </div>
        </div>
      )}

      <div className="mt-2">
        <Label className="text-[11px]">What's included / notes</Label>
        <Textarea value={pkg.includes || ''} onChange={(e) => onPatch({ includes: e.target.value })} rows={2} placeholder={isHourly ? 'e.g. general SMM support for Bec' : 'e.g. 8 IG posts, 12 reels, monthly strategy call…'} className="mt-1 text-sm" />
      </div>

      {isHourly && (
        <div className="mt-3 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="w-full flex items-center justify-between px-3 py-2 text-sm bg-lilac-50/40 border-b border-slate-100">
            <span className="inline-flex items-center gap-1.5 text-slate-700">
              <Clock className="w-3.5 h-3.5 text-lilac-600" />
              <span className="font-medium">monthly hours log</span>
              <span className="text-xs text-slate-500">— {totalHours}h logged · {fmtMoney(totalHourlyValue, currency)}</span>
            </span>
          </div>
          <div className="p-3 space-y-2">
            {(pkg.hours_log || []).length === 0 && (
              <div className="text-xs text-slate-400 italic">no hours logged yet — click below to add a month</div>
            )}
            {(pkg.hours_log || []).map((h, idx) => (
              <div key={h.id || idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start" data-testid={`hours-row-${idx}`}>
                <div className="md:col-span-3">
                  <Label className="text-[10px]">Month</Label>
                  <Input type="month" value={h.month || ''} onChange={(e) => patchLog(idx, { month: e.target.value })} className="mt-1 h-8 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px]">Hours</Label>
                  <NumberInput value={h.hours} onChange={(v) => patchLog(idx, { hours: Number(v) || 0 })} className="mt-1 h-8 text-sm" data-testid={`hours-input-${idx}`} />
                </div>
                <div className="md:col-span-2 flex flex-col">
                  <Label className="text-[10px]">Amount</Label>
                  <div className="mt-1 h-8 text-sm font-semibold text-slate-700 flex items-center">{fmtMoney((Number(h.hours) || 0) * (Number(pkg.hourly_rate) || 0), currency)}</div>
                </div>
                <div className="md:col-span-4">
                  <Label className="text-[10px]">Notes</Label>
                  <Input value={h.notes || ''} onChange={(e) => patchLog(idx, { notes: e.target.value })} placeholder="what was done" className="mt-1 h-8 text-sm" />
                </div>
                <div className="md:col-span-1 flex items-end justify-end">
                  <button onClick={() => removeLog(idx)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="remove"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
            <Button variant="ghost" size="sm" onClick={addLog} className="border border-dashed border-slate-300 text-slate-600 hover:border-pink-300 hover:text-pink-700 mt-1" data-testid={`add-hours-${pkg.id}`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> log hours for a month
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Ad-hoc project editor ----------
const ProjectEditor = ({ proj, onPatch, onRemove, currency }) => (
  <div className="bg-lilac-50/40 border border-lilac-100 rounded-xl p-3" data-testid={`proj-editor-${proj.id}`}>
    <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-start">
      <div className="md:col-span-2">
        <Label className="text-[11px]">Project title</Label>
        <Input value={proj.title || ''} onChange={(e) => onPatch({ title: e.target.value })} placeholder="e.g. Rin — spring campaign" className="mt-1 h-8 text-sm" data-testid={`proj-title-${proj.id}`} />
      </div>
      <div>
        <Label className="text-[11px]">Fee</Label>
        <NumberInput value={proj.fee} onChange={(v) => onPatch({ fee: Number(v) || 0 })} className="mt-1 h-8 text-sm" />
      </div>
      <div>
        <Label className="text-[11px]">Bill in month</Label>
        <Input type="month" value={proj.bill_month || ''} onChange={(e) => onPatch({ bill_month: e.target.value })} className="mt-1 h-8 text-sm" data-testid={`proj-bill-month-${proj.id}`} />
      </div>
      <div>
        <Label className="text-[11px]">Due</Label>
        <Input type="date" value={proj.due_date || ''} onChange={(e) => onPatch({ due_date: e.target.value })} className="mt-1 h-8 text-sm" />
      </div>
      <div>
        <Label className="text-[11px]">Status</Label>
        <Select value={proj.status || 'todo'} onValueChange={(v) => onPatch({ status: v })}>
          <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{PROJECT_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex items-end justify-end">
        <button onClick={onRemove} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="remove project"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
    <div className="mt-2">
      <Label className="text-[11px]">Notes / deliverables</Label>
      <Textarea value={proj.notes || ''} onChange={(e) => onPatch({ notes: e.target.value })} rows={2} placeholder="scope, deliverables, drive links…" className="mt-1 text-sm" />
    </div>
  </div>
);

// ---------- Main dialog ----------
const ClientDialog = ({ open, onOpenChange, client, onSaved }) => {
  const [form, setForm] = useState(emptyClient());
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(client?.id);

  useEffect(() => {
    setForm(client ? { ...emptyClient(), ...client, packages: client.packages || [], ad_hoc_projects: client.ad_hoc_projects || [] } : emptyClient());
  }, [client, open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const addPkg = (type = 'retainer') => set('packages', [...(form.packages || []), emptyPkg(type)]);
  const patchPkg = (idx, patch) => set('packages', form.packages.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const removePkg = (idx) => set('packages', form.packages.filter((_, i) => i !== idx));

  const addProj = () => set('ad_hoc_projects', [...(form.ad_hoc_projects || []), emptyProject()]);
  const patchProj = (idx, patch) => set('ad_hoc_projects', form.ad_hoc_projects.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const removeProj = (idx) => set('ad_hoc_projects', form.ad_hoc_projects.filter((_, i) => i !== idx));

  const retainerTotal = (form.packages || []).filter((p) => (p.type || 'retainer') === 'retainer').reduce((s, p) => s + (Number(p.price) || 0), 0);
  const hourlyThisMonth = (form.packages || []).filter((p) => p.type === 'hourly').reduce((s, p) => {
    const rate = Number(p.hourly_rate) || 0;
    const hrs = (p.hours_log || []).filter((h) => h.month === todayMonth()).reduce((a, h) => a + (Number(h.hours) || 0), 0);
    return s + hrs * rate;
  }, 0);
  const adHocThisMonth = (form.ad_hoc_projects || []).reduce((s, p) => (p.bill_month || p.due_date || p.created_at || '').slice(0, 7) === todayMonth() ? s + (Number(p.fee) || 0) : s, 0);
  const monthlyTotal = retainerTotal + hourlyThisMonth + adHocThisMonth;

  const save = async () => {
    setSaving(true);
    try {
      // everything is optional — name defaults to "untitled client"
      const payload = { ...form, name: (form.name || '').trim() || 'untitled client' };
      if (isEdit) {
        const { data } = await api.patch(`/clients/${client.id}`, payload);
        onSaved(data);
      } else {
        const { data } = await api.post('/clients', payload);
        onSaved(data);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'edit client' : 'add client'}</DialogTitle></DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          <div className="md:col-span-2 flex items-end gap-3">
            <div>
              <Label>Emoji</Label>
              <div className="mt-1">
                <EmojiPicker value={form.logo || ''} onChange={(v) => set('logo', v)} size="lg" testId="client-emoji-picker" />
              </div>
            </div>
            <div className="flex-1"><Label>Client name</Label><Input autoFocus value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Rebecca September" data-testid="client-name-input" /></div>
          </div>
          <div><Label>Contact name</Label><Input value={form.contact_name || ''} onChange={(e) => set('contact_name', e.target.value)} /></div>
          <div><Label>Handle</Label><Input value={form.handle || ''} onChange={(e) => set('handle', e.target.value)} placeholder="@rebeccaseptember" /></div>
          <div><Label>Email</Label><Input type="email" value={form.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} /></div>
          <div><Label>Phone</Label><Input value={form.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} /></div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set('status', v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Currency</Label>
            <Select value={form.currency} onValueChange={(v) => set('currency', v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="GBP">£ GBP</SelectItem><SelectItem value="USD">$ USD</SelectItem><SelectItem value="EUR">€ EUR</SelectItem></SelectContent>
            </Select>
          </div>
          {form.status === 'ad_hoc' && (
            <>
              <div>
                <Label>Active from</Label>
                <Input type="date" value={form.active_from || ''} onChange={(e) => set('active_from', e.target.value)} data-testid="client-active-from" />
              </div>
              <div>
                <Label>Active to <span className="text-slate-400 text-[11px]">(optional)</span></Label>
                <Input type="date" value={form.active_to || ''} onChange={(e) => set('active_to', e.target.value)} data-testid="client-active-to" />
              </div>
            </>
          )}
          <div className="md:col-span-2"><Label>Notes</Label><Textarea value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={2} /></div>
          <div className="md:col-span-2">
            <Label>Insights reporting</Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="insight-types">
              {[
                { id: 'organic', label: 'organic social', emoji: '🌱' },
                { id: 'influencer', label: 'mailer performance', emoji: '🎁' },
              ].map((t) => {
                const on = (form.insight_types || []).includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      const list = form.insight_types || [];
                      set('insight_types', on ? list.filter((x) => x !== t.id) : [...list, t.id]);
                    }}
                    className={`h-8 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 border transition ${on ? 'bg-pink-50 border-pink-300 text-pink-700' : 'bg-white border-slate-200 text-slate-500 hover:border-pink-200 hover:text-pink-600'}`}
                    data-testid={`insight-type-${t.id}`}
                  >
                    <span>{t.emoji}</span>{t.label}
                    {on && <span className="text-pink-500 text-[13px] leading-none">✓</span>}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-slate-400 mt-1">tick each report type you want this client to appear under on the insights page.</div>
          </div>
        </div>

        {/* Packages */}
        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-slate-700">packages</div>
              <div className="text-xs text-slate-500">add retainer (monthly £) or hourly (£/hr with a monthly hours log)</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">this month</div>
              <div className="text-lg font-semibold text-slate-900" data-testid="client-total">{fmtMoney(monthlyTotal, form.currency)}</div>
              {hourlyThisMonth > 0 && <div className="text-[10px] text-slate-400">retainer {fmtMoney(retainerTotal, form.currency)} + hourly {fmtMoney(hourlyThisMonth, form.currency)}</div>}
            </div>
          </div>

          {(form.packages || []).length === 0 && <div className="text-xs text-slate-400 italic">no packages yet — add one below</div>}

          <div className="space-y-3">
            {(form.packages || []).map((pkg, idx) => (
              <PackageEditor
                key={pkg.id || idx}
                pkg={pkg}
                currency={form.currency}
                onPatch={(patch) => patchPkg(idx, patch)}
                onRemove={() => removePkg(idx)}
              />
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <Button variant="ghost" onClick={() => addPkg('retainer')} className="border border-dashed border-slate-300 text-slate-600 hover:border-pink-300 hover:text-pink-700" data-testid="add-retainer-btn">
              <Plus className="w-4 h-4 mr-1" /> add retainer
            </Button>
            <Button variant="ghost" onClick={() => addPkg('hourly')} className="border border-dashed border-slate-300 text-slate-600 hover:border-lilac-400 hover:text-lilac-700" data-testid="add-hourly-btn">
              <Clock className="w-4 h-4 mr-1" /> add hourly
            </Button>
          </div>
        </div>

        {/* Ad-hoc projects */}
        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-slate-700">ad-hoc projects</div>
              <div className="text-xs text-slate-500">one-off projects billed separately from the retainer</div>
            </div>
          </div>
          {(form.ad_hoc_projects || []).length === 0 && <div className="text-xs text-slate-400 italic">no ad-hoc projects yet</div>}
          <div className="space-y-3">
            {(form.ad_hoc_projects || []).map((proj, idx) => (
              <ProjectEditor
                key={proj.id || idx}
                proj={proj}
                currency={form.currency}
                onPatch={(patch) => patchProj(idx, patch)}
                onRemove={() => removeProj(idx)}
              />
            ))}
          </div>
          <Button variant="ghost" onClick={addProj} className="mt-3 border border-dashed border-slate-300 text-slate-600 hover:border-lilac-400 hover:text-lilac-700" data-testid="add-project-btn">
            <Plus className="w-4 h-4 mr-1" /> add ad-hoc project
          </Button>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>cancel</Button>
          <Button disabled={saving} onClick={save} className="bg-pink-500 hover:bg-pink-600 text-white font-semibold" data-testid="save-client-btn">
            {saving ? 'saving…' : (isEdit ? 'save changes' : 'add client')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StatusPill = ({ status }) => {
  const s = STATUSES.find((x) => x.id === status) || STATUSES[0];
  return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700"><span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />{s.label}</span>;
};

// Colour swatch button shown next to a client's name. Clicking opens a picker
// with the preset palette and a hex input, so any brand-specific colour can be used.
const ClientColorDot = ({ client, onChange }) => {
  const current = clientColorFor(client);
  const swatch = stickyColorFor(current);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [hex, setHex] = useState(isHexColor(current) ? normalizeHex(current) : '');
  const btnRef = useRef(null);
  useEffect(() => { setHex(isHexColor(current) ? normalizeHex(current) : ''); }, [current]);

  const openPicker = (e) => {
    e.stopPropagation();
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setAnchor({ top: r.bottom + 4, left: r.left });
    }
    setOpen((o) => !o);
  };
  const pick = (value) => { onChange(value); setOpen(false); };
  const commitHex = () => {
    const norm = normalizeHex(hex);
    if (norm) { pick(norm); }
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={openPicker}
        title="click to change this client's colour code"
        className="shrink-0 w-4 h-4 mt-1 rounded-full ring-2 ring-white shadow-sm hover:scale-110 transition-transform"
        style={{ background: swatch.tape }}
        data-testid={`client-color-dot-${client.id}`}
      />
      {open && anchor && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[9999] w-64 bg-white rounded-lg shadow-xl border border-slate-200 p-3"
            style={{ top: anchor.top, left: anchor.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">preset palette</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {STICKY_COLORS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => pick(c.id)}
                  className={`w-7 h-7 rounded-full ring-2 shadow-sm hover:scale-110 transition-transform ${current === c.id ? 'ring-slate-800' : 'ring-white'}`}
                  style={{ background: c.tape }}
                  title={c.id}
                  data-testid={`client-color-preset-${c.id}`}
                />
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">brand hex code</div>
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-full ring-2 ring-white shadow-sm shrink-0" style={{ background: normalizeHex(hex) || '#e2e8f0' }} />
              <input
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') commitHex(); }}
                placeholder="#ff88aa"
                className="h-8 flex-1 text-xs border border-slate-200 rounded px-2 outline-none focus:ring-2 focus:ring-pink-200"
                data-testid={`client-color-hex-input-${client.id}`}
              />
              <input
                type="color"
                value={normalizeHex(hex) || '#f9a8c8'}
                onChange={(e) => setHex(e.target.value)}
                className="w-8 h-8 border border-slate-200 rounded cursor-pointer"
                title="pick from wheel"
              />
              <button
                onClick={commitHex}
                disabled={!isHexColor(hex)}
                className="text-[11px] font-semibold text-white bg-pink-500 hover:bg-pink-600 disabled:opacity-50 rounded px-2 py-1"
                data-testid={`client-color-hex-save-${client.id}`}
              >save</button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
};


const ProjectStatusPill = ({ status }) => {
  const s = PROJECT_STATUSES.find((x) => x.id === status) || PROJECT_STATUSES[0];
  return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium" style={{ background: s.color + '22', color: s.color }}><span className="w-1 h-1 rounded-full" style={{ background: s.color }} />{s.label}</span>;
};

// Value contributed by a package this month
const packageMonthlyValue = (p, month) => {
  if (p.type === 'hourly') {
    const rate = Number(p.hourly_rate) || 0;
    const hrs = (p.hours_log || []).filter((h) => h.month === month).reduce((a, h) => a + (Number(h.hours) || 0), 0);
    return hrs * rate;
  }
  // retainer: check date window — compared by month (not exact day), so a package that
  // started or ended mid-month still counts for that whole month once it's edited.
  if (p.start_date && month < p.start_date.slice(0, 7)) return 0;
  if (p.end_date && month > p.end_date.slice(0, 7)) return 0;
  return Number(p.price) || 0;
};

// Sum a package's projected value across all 12 months of the given year (respects start/end for retainers, sums all logged hours for hourly)
const packageYearProjection = (p, year) => {
  if (p.type === 'hourly') {
    const rate = Number(p.hourly_rate) || 0;
    return (p.hours_log || []).reduce((sum, h) => {
      if (!(h.month || '').startsWith(`${year}-`)) return sum;
      return sum + (Number(h.hours) || 0) * rate;
    }, 0);
  }
  // retainer: count every month in the year that falls inside start_date..end_date
  let total = 0;
  for (let mi = 1; mi <= 12; mi++) {
    const m = `${year}-${String(mi).padStart(2, '0')}`;
    total += packageMonthlyValue(p, m);
  }
  return total;
};

// Ad-hoc revenue projection for a year: sum of every project whose billing month lands in that year
const clientAdHocProjection = (c, year) => (c.ad_hoc_projects || []).reduce((s, p) => {
  const bm = projectMonth(p);
  return bm.slice(0, 4) === String(year) ? s + (Number(p.fee) || 0) : s;
}, 0);

// Ad-hoc project's billing month — the single month the user entered it for (bill_month),
// falling back to its due date / created month for older records.
const projectMonth = (p) => (p.bill_month || p.due_date || p.created_at || '').slice(0, 7);

// Ad-hoc fees billed in a specific month for a client
const clientAdHocForMonth = (c, month) => (c.ad_hoc_projects || []).reduce((s, p) => projectMonth(p) === month ? s + (Number(p.fee) || 0) : s, 0);

// Total client value for a month = packages (retainer window + hourly logged) + ad-hoc billed that month
const clientMonthlyValue = (c, month) => (c.packages || []).reduce((a, p) => a + packageMonthlyValue(p, month), 0) + clientAdHocForMonth(c, month);

const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(todayMonth());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clients');
      setClients(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const thisMonth = selectedMonth;
  const year = Number(selectedMonth.slice(0, 4));
  const isCurrentMonth = selectedMonth === todayMonth();

  const totals = useMemo(() => {
    const activeCount = clients.filter((c) => c.status === 'active').length;
    // Monthly billing counts active + ad-hoc clients: packages in their date window + ad-hoc fees billed this month
    const billing = clients.filter((c) => c.status === 'active' || c.status === 'ad_hoc');
    const monthly = billing.reduce((s, c) => s + clientMonthlyValue(c, thisMonth), 0);
    // Full-year projection across ALL clients — retainers count every active month, hourly counts all logged hours, ad-hoc counts each project in its billing month
    const projection = clients.reduce((s, c) => {
      const pkgProj = (c.packages || []).reduce((a, p) => a + packageYearProjection(p, year), 0);
      const adHocProj = clientAdHocProjection(c, year);
      return s + pkgProj + adHocProj;
    }, 0);
    return { count: activeCount, monthly, projection };
  }, [clients, thisMonth, year]);

  if (!['manager', 'talent_manager'].includes(user?.role) && user?.role !== 'social_manager') {
    return <div className="p-8 text-center text-slate-500">access denied</div>;
  }

  const exportClients = async () => {
    try {
      const { data } = await api.get('/clients/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `lvesocial-clients-${stamp}.json`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`exported ${(data.clients || []).length} client${(data.clients || []).length === 1 ? '' : 's'}`);
    } catch { toast.error('export failed'); }
  };

  const importClients = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const arr = Array.isArray(parsed) ? parsed : (parsed.clients || []);
      if (!arr.length) { toast.error('no clients in that file'); return; }
      const mode = window.confirm(
        `Import ${arr.length} client${arr.length === 1 ? '' : 's'}.\n\nOK  = overwrite matching ids\nCancel = skip existing ids (add only new)`,
      ) ? 'overwrite' : 'skip_existing';
      const { data } = await api.post('/clients/import', { clients: arr, mode });
      toast.success(`imported: ${data.inserted} new · ${data.updated} updated · ${data.skipped} skipped`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'import failed — check the file'); }
  };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setDialogOpen(true); };
  const updateClientColor = async (id, color) => {
    try {
      const { data } = await api.patch(`/clients/${id}`, { color });
      setClients((prev) => prev.map((c) => c.id === id ? data : c));
    } catch { toast.error('failed to change colour'); }
  };
  // Tick/untick "invoiced this month" at the whole-client level — also ticks/unticks every package.
  const toggleClientInvoiced = async (c, month) => {
    const has = (c.manually_invoiced_months || []).includes(month);
    const manually_invoiced_months = has
      ? (c.manually_invoiced_months || []).filter((m) => m !== month)
      : [...new Set([...(c.manually_invoiced_months || []), month])];
    const packages = (c.packages || []).map((p) => {
      const pm = p.invoiced_months || [];
      return { ...p, invoiced_months: has ? pm.filter((m) => m !== month) : [...new Set([...pm, month])] };
    });
    try {
      const { data } = await api.patch(`/clients/${c.id}`, { manually_invoiced_months, packages });
      setClients((prev) => prev.map((x) => x.id === c.id ? data : x));
    } catch { toast.error('failed to update'); }
  };
  // Tick/untick a single package for the month; keep the client-level tick in sync (ticked only when every package is).
  const togglePackageInvoiced = async (c, pkgId, month) => {
    const packages = (c.packages || []).map((p) => {
      if (p.id !== pkgId) return p;
      const pm = p.invoiced_months || [];
      const has = pm.includes(month);
      return { ...p, invoiced_months: has ? pm.filter((m) => m !== month) : [...new Set([...pm, month])] };
    });
    const allTicked = packages.length > 0 && packages.every((p) => (p.invoiced_months || []).includes(month));
    const base = (c.manually_invoiced_months || []).filter((m) => m !== month);
    const manually_invoiced_months = allTicked ? [...base, month] : base;
    try {
      const { data } = await api.patch(`/clients/${c.id}`, { packages, manually_invoiced_months });
      setClients((prev) => prev.map((x) => x.id === c.id ? data : x));
    } catch { toast.error('failed to update'); }
  };
  const onSaved = (data) => {
    setClients((prev) => (prev.find((c) => c.id === data.id) ? prev.map((c) => c.id === data.id ? data : c) : [data, ...prev]));
    toast.success(editing ? 'client updated' : 'client added');
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/clients/${c.id}`);
    setClients((prev) => prev.filter((x) => x.id !== c.id));
  };

  const invoiceProject = async (client, proj) => {
    try {
      const { data } = await api.post(`/clients/${client.id}/projects/${proj.id}/generate-invoice`);
      toast.success(`draft invoice ${data.number} created`, { description: `for ${proj.title || 'project'} — find it in Invoicing` });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'failed');
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="clients-page">
      <div className="flex items-center gap-3 mb-4">
        <Briefcase className="w-6 h-6 text-pink-500" />
        <h1 className="text-2xl font-semibold text-slate-900">Clients</h1>
        <span className="text-sm text-slate-500">social media management</span>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={exportClients} variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="export-clients-btn" title="download all clients as JSON">
            <Download className="w-4 h-4 mr-1.5" /> export
          </Button>
          <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100 cursor-pointer text-sm" title="load clients from a previously exported JSON file">
            <Upload className="w-4 h-4" /> import
            <input
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importClients(f); }}
              data-testid="import-clients-input"
            />
          </label>
          <Button onClick={openNew} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-1.5" /> add client
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4" data-testid="clients-month-nav">
        <button
          onClick={() => setSelectedMonth(shiftMonth(selectedMonth, -1))}
          className="p-1.5 rounded-md text-slate-500 hover:text-pink-600 hover:bg-pink-50 border border-slate-200"
          title="previous month"
          data-testid="clients-month-prev"
        ><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-sm font-semibold text-slate-800 min-w-[110px] text-center" data-testid="clients-month-label">{monthLabel(selectedMonth)}</div>
        <button
          onClick={() => setSelectedMonth(shiftMonth(selectedMonth, 1))}
          className="p-1.5 rounded-md text-slate-500 hover:text-pink-600 hover:bg-pink-50 border border-slate-200"
          title="next month"
          data-testid="clients-month-next"
        ><ChevronRight className="w-4 h-4" /></button>
        {!isCurrentMonth && (
          <button
            onClick={() => setSelectedMonth(todayMonth())}
            className="h-7 px-2.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 hover:bg-pink-100 hover:text-pink-700"
            data-testid="clients-month-today"
          >back to today</button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6 max-w-3xl">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">active clients</div>
          <div className="text-2xl font-semibold text-slate-900 mt-1">{totals.count}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">{monthLabel(selectedMonth)}</div>
          <div className="text-2xl font-semibold text-pink-600 mt-1" data-testid="clients-month-total">{fmtMoney(totals.monthly)}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">retainers + hours logged</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">{year} projection</div>
          <div className="text-2xl font-semibold text-lilac-700 mt-1" data-testid="clients-projection">{fmtMoney(totals.projection)}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">retainers × active months + hours + projects</div>
        </div>
      </div>


      {loading ? <div className="text-sm text-slate-400">loading…</div> : (
        clients.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-500">no clients yet — click "add client" to add your first</div>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map((c) => {
              const monthly = clientMonthlyValue(c, thisMonth);
              // Ad-hoc projects are tracked in exactly the month they're billed in — show the
              // ones whose billing month matches the month being viewed.
              const openProjects = (c.ad_hoc_projects || []).filter((p) => projectMonth(p) === selectedMonth);
              const swatch = stickyColorFor(clientColorFor(c));
              return (
                <div key={c.id} className="bg-white border rounded-xl overflow-hidden transition" style={{ borderColor: swatch.border }} data-testid={`client-card-${c.id}`}>
                  <div className="p-4 flex items-start gap-4" style={{ background: swatch.bg, borderBottom: `1px solid ${swatch.border}` }}>
                    <ClientColorDot client={c} onChange={(color) => updateClientColor(c.id, color)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold truncate" style={{ color: swatch.ink }}>{c.name}</h3>
                        <StatusPill status={c.status} />
                        {c.handle && <span className="text-xs" style={{ color: swatch.ink, opacity: 0.7 }}>{c.handle}</span>}
                      </div>
                      {(c.contact_name || c.contact_email || c.contact_phone) && (
                        <div className="text-xs mt-0.5" style={{ color: swatch.ink, opacity: 0.75 }}>
                          {[c.contact_name, c.contact_email, c.contact_phone].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {c.status === 'ad_hoc' && (c.active_from || c.active_to) && (
                        <div className="text-[11px] mt-0.5" style={{ color: swatch.ink, opacity: 0.75 }}>
                          active {c.active_from ? `from ${fmtDate(c.active_from)}` : ''}{c.active_to ? ` until ${fmtDate(c.active_to)}` : ''}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase tracking-wide" style={{ color: swatch.ink, opacity: 0.6 }}>{isCurrentMonth ? 'this month' : monthLabel(selectedMonth)}</div>
                      <div className="text-lg font-semibold" style={{ color: swatch.ink }}>{fmtMoney(monthly, c.currency)}</div>
                    </div>
                    <label className="flex flex-col items-center gap-0.5 shrink-0 cursor-pointer select-none px-1" title={`mark invoiced for ${monthLabel(selectedMonth)}`}>
                      <input
                        type="checkbox"
                        checked={(c.manually_invoiced_months || []).includes(selectedMonth)}
                        onChange={() => toggleClientInvoiced(c, selectedMonth)}
                        className="w-4 h-4 accent-pink-500 cursor-pointer"
                        data-testid={`client-invoiced-${c.id}`}
                      />
                      <span className="text-[9px] font-medium uppercase tracking-wide" style={{ color: swatch.ink, opacity: 0.7 }}>invoiced</span>
                    </label>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/clients/${c.id}/generate-invoice`);
                            toast.success(`draft invoice ${data.number} created`, { description: 'find it in Invoicing → Client invoices' });
                          } catch (e) { toast.error(e?.response?.data?.detail || 'failed'); }
                        }}
                        className="p-1.5 text-slate-400 hover:text-pink-600 hover:bg-pink-50 rounded"
                        title="generate this month's invoice"
                        data-testid={`gen-invoice-${c.id}`}
                      ><FileText className="w-4 h-4" /></button>
                      <button onClick={() => openEdit(c)} className="p-1.5 text-slate-400 hover:text-slate-800 rounded" title="edit" data-testid={`edit-client-${c.id}`}><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => remove(c)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>

                  {(c.packages || []).length > 0 && (
                    <div className="border-t border-slate-100 divide-y divide-slate-100">
                      {c.packages.map((p) => {
                        const value = packageMonthlyValue(p, thisMonth);
                        const isHourly = p.type === 'hourly';
                        const hrs = isHourly ? (p.hours_log || []).filter((h) => h.month === thisMonth).reduce((a, h) => a + (Number(h.hours) || 0), 0) : 0;
                        const ended = p.end_date && thisMonth + '-01' > p.end_date.slice(0, 10);
                        const notYetStarted = p.start_date && thisMonth + '-01' < p.start_date.slice(0, 10);
                        return (
                          <div key={p.id} className="px-4 py-2.5 flex items-start gap-3 text-sm">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 font-medium text-slate-700 truncate">
                                {isHourly ? <Clock className="w-3.5 h-3.5 text-lilac-600" /> : <Zap className="w-3.5 h-3.5 text-pink-500" />}
                                <span className="truncate">{p.name || (isHourly ? 'Hourly' : 'Retainer')}</span>
                                {ended && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">ended</span>}
                                {notYetStarted && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">upcoming</span>}
                              </div>
                              {p.includes && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{p.includes}</div>}
                              {(p.start_date || p.end_date) && (
                                <div className="text-[10px] text-slate-400 mt-0.5">
                                  {p.start_date ? `from ${fmtDate(p.start_date)}` : ''}{p.start_date && p.end_date ? ' ' : ''}{p.end_date ? `until ${fmtDate(p.end_date)}` : ''}
                                </div>
                              )}
                            </div>
                            {isHourly && (
                              <div className="text-right shrink-0">
                                <div className="text-[10px] uppercase text-slate-400">{monthLabel(thisMonth)}</div>
                                <div className="text-xs text-slate-600">{hrs}h @ {fmtMoney(p.hourly_rate || 0, c.currency)}/hr</div>
                              </div>
                            )}
                            {!isHourly && p.roll_date && (
                              <div className="text-right shrink-0">
                                <div className="text-[10px] uppercase text-slate-400">rolls</div>
                                <div className="text-xs text-slate-600">{fmtDate(p.roll_date)}</div>
                              </div>
                            )}
                            <div className="text-right shrink-0 min-w-[90px]">
                              <div className="text-sm font-semibold text-slate-900">{fmtMoney(value, c.currency)}</div>
                              <div className="text-[10px] text-slate-400">/ {isHourly ? 'this month' : 'month'}</div>
                            </div>
                            <label className="flex flex-col items-center gap-0.5 shrink-0 cursor-pointer select-none" title={`invoiced for ${monthLabel(thisMonth)}`}>
                              <input
                                type="checkbox"
                                checked={(p.invoiced_months || []).includes(thisMonth)}
                                onChange={() => togglePackageInvoiced(c, p.id, thisMonth)}
                                className="w-4 h-4 accent-pink-500 cursor-pointer"
                                data-testid={`pkg-invoiced-${p.id}`}
                              />
                              <span className="text-[9px] text-slate-400 uppercase tracking-wide">inv</span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {openProjects.length > 0 && (
                    <div className="border-t border-slate-100 bg-lilac-50/30">
                      <div className="px-4 py-2 text-[11px] uppercase tracking-wide text-lilac-700 font-medium">{isCurrentMonth ? 'ad-hoc projects' : `ad-hoc projects billed in ${monthLabel(selectedMonth)}`}</div>
                      <div className="divide-y divide-slate-100">
                        {openProjects.map((proj) => (
                          <div key={proj.id} className="px-4 py-2.5 flex items-start gap-3 text-sm" data-testid={`project-row-${proj.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800 truncate">{proj.title || 'untitled project'}</span>
                                <ProjectStatusPill status={proj.status} />
                              </div>
                              {proj.notes && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{proj.notes}</div>}
                              {proj.due_date && <div className="text-[10px] text-slate-400 mt-0.5">due {fmtDate(proj.due_date)}</div>}
                            </div>
                            <div className="text-right shrink-0 min-w-[80px]">
                              <div className="text-sm font-semibold text-slate-900">{fmtMoney(proj.fee || 0, c.currency)}</div>
                            </div>
                            <button
                              onClick={() => invoiceProject(c, proj)}
                              className="p-1.5 text-slate-400 hover:text-pink-600 hover:bg-pink-50 rounded shrink-0"
                              title="generate invoice for this project"
                              data-testid={`invoice-project-${proj.id}`}
                            ><FileText className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      <ClientDialog open={dialogOpen} onOpenChange={setDialogOpen} client={editing} onSaved={onSaved} />
    </div>
  );
};

export default Clients;
